import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SocialAccount } from '../api/social_api';
import type { BookOrder, FableCharacter, VoiceRecord } from '../api/network_calls';
import type { TrialOffer } from '../util/trialOffer';
import type { EditStoryDraftCache } from '../util/editStoryDraftState';
import {
  mergeCharactersPreservingPending,
  mergeVoicesPreservingPending,
} from '../util/generationRecords';

const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface User {
  email: string | null;
  userId: string | null;
  tier: string | null;
  tokens: number;
  character_tokens: number;
  email_verified: boolean | null;
  is_platinum: boolean;
  is_premium: boolean;
  is_trial: boolean;
  trial_end_date: number | null;
  has_used_trial: boolean;
  web_subscription: boolean;
}

interface Story {
  storyId: string;
  rootStoryTitle?: string;
  storyTitle?: string;
  storyName?: string;
  chapterTitle?: string;
  title?: string;
  name?: string;
  createDate: number;
  imageUrls?: string[];
  thumbnailUrl?: string;
  audioUrl?: string;
  audioUrls?: string[];
  chapterIds?: string[];
  chapters?: Story[];
  pending?: boolean;
  pendingChapterCount?: number;
  // Add other story properties as needed
}

interface StoryLibraryCacheQuery {
  sortOrder?: 'newest' | 'oldest';
  storyModes: Array<'Regular' | 'Dialogue' | 'Musical'>;
  storyLengths: Array<'Normal' | 'Long'>;
  storyReaders: Array<'Toddler' | 'Teen' | 'Adult'>;
}

interface RefreshStoriesPayload {
  stories: Story[];
  last_refreshed_at: number | null;
  last_story_edited_at: number | null;
  library_query?: StoryLibraryCacheQuery;
  total_story_count?: number | null;
}

type Character = FableCharacter;
type Voice = VoiceRecord;

interface Notifications {
  story_updates: boolean;
  fable_updates: boolean;
  reminders?: boolean;
}

interface Rewards {
  streak: {
    current: number;
    max: number;
    reward: number;
    type: string;
    lastVisitTime: number;
    cooldownTime: number;
  };
  stories: {
    current: number;
    max: number;
    reward: number;
    type: string;
  };
}

interface AppState {
  // User data
  email: string | null;
  userId: string | null;
  tier: string | null;
  membershipLoaded: boolean;
  tokens: number;
  character_tokens: number;
  email_verified: boolean | null;
  is_platinum: boolean;
  is_premium: boolean;
  is_trial: boolean;
  trial_end_date: number | null;
  has_used_trial: boolean;
  web_subscription: boolean;
  trialOffer: TrialOffer | null;
  
  // Stories
  stories: Story[];
  last_refreshed_at: number | null;
  last_story_edited_at: number | null;
  lastEvaluatedKey: any | null;
  donePaginating: boolean;
  story_library_query: StoryLibraryCacheQuery | null;
  story_total_count: number | null;
  editStoryDrafts: Record<string, EditStoryDraftCache>;
  
  // App state
  onboarding_seen: boolean;
  gambitBannerDismissedByUser: Record<string, boolean>;
  characters: Character[];
  characters_user_id: string | null;
  characters_refreshed_at: number | null;
  voices: Voice[];
  voices_user_id: string | null;
  voices_refreshed_at: number | null;
  book_orders: BookOrder[];
  book_orders_user_id: string | null;
  book_orders_refreshed_at: number | null;
  last_reviewed_at: number | null;
  social_accounts: SocialAccount[];
  social_accounts_refreshed_at: number | null;
  
  // Notifications
  notifications: Notifications;
  
  // Campaign
  campaignScheduled: boolean;
  
  // Rewards
  rewards: Rewards;
}

const initialState: AppState = {
  email: null,
  userId: null,
  tier: null,
  membershipLoaded: false,
  tokens: 0,
  character_tokens: 0,
  email_verified: null,
  is_platinum: false,
  is_premium: false,
  is_trial: false,
  trial_end_date: null,
  has_used_trial: false,
  web_subscription: false,
  trialOffer: null,
  stories: [],
  last_refreshed_at: null,
  last_story_edited_at: null,
  lastEvaluatedKey: null,
  donePaginating: false,
  story_library_query: null,
  story_total_count: null,
  editStoryDrafts: {},
  onboarding_seen: false,
  gambitBannerDismissedByUser: {},
  characters: [],
  characters_user_id: null,
  characters_refreshed_at: null,
  voices: [],
  voices_user_id: null,
  voices_refreshed_at: null,
  book_orders: [],
  book_orders_user_id: null,
  book_orders_refreshed_at: null,
  last_reviewed_at: null,
  social_accounts: [],
  social_accounts_refreshed_at: null,
  notifications: {
    story_updates: false,
    fable_updates: false,
  },
  campaignScheduled: false,
  rewards: {
    streak: {
      current: 1,
      max: 5,
      reward: 1,
      type: 'story_token',
      lastVisitTime: Date.now(),
      cooldownTime: ONE_DAY_MS,
    },
    stories: {
      current: 0,
      max: 25,
      reward: 3,
      type: 'story_token',
    },
  },
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    dismiss_gambit_banner: (state) => {
      if (state.userId) state.gambitBannerDismissedByUser[state.userId] = true;
    },
    set_user: (state, action: PayloadAction<User>) => {
      console.debug('dispatched set_user', action.payload);
      const previousUserId = state.userId;
      const retainMembership = !!action.payload.userId
        && previousUserId === action.payload.userId && state.membershipLoaded;
      state.email = action.payload.email;
      state.userId = action.payload.userId;
      state.tokens = action.payload.tokens;
      state.character_tokens = action.payload.character_tokens;
      state.email_verified = action.payload.email_verified;
      // Identity refreshes carry legacy Cognito flags, not the authoritative
      // billing tier. Keep the current account's membership until billing updates it.
      if (!retainMembership) {
        state.tier = action.payload.tier;
        state.membershipLoaded = false;
        state.is_platinum = action.payload.is_platinum;
        state.is_premium = action.payload.is_premium;
        state.is_trial = action.payload.is_trial;
        state.trial_end_date = action.payload.trial_end_date;
        state.has_used_trial = action.payload.has_used_trial;
        state.web_subscription = action.payload.web_subscription;
      }
      if (previousUserId !== action.payload.userId) {
        state.editStoryDrafts = {};
        state.social_accounts = [];
        state.social_accounts_refreshed_at = null;
        state.trialOffer = null;
        const nextUserId = action.payload.userId;
        if (!nextUserId || (state.characters.length > 0 && state.characters_user_id !== nextUserId)) {
          state.characters = [];
          state.characters_user_id = null;
          state.characters_refreshed_at = null;
        }
        if (!nextUserId || (state.voices.length > 0 && state.voices_user_id !== nextUserId)) {
          state.voices = [];
          state.voices_user_id = null;
          state.voices_refreshed_at = null;
        }
        if (!nextUserId || state.book_orders_user_id !== nextUserId) {
          state.book_orders = [];
          state.book_orders_user_id = null;
          state.book_orders_refreshed_at = null;
        }
      }
    },
    update_tokens: (state, action: PayloadAction<{ tokens: number }>) => {
      console.debug('dispatched update_tokens', action.payload);
      state.tokens = action.payload.tokens;
    },
    update_billing_account: (state, action: PayloadAction<{ userId: string } & Partial<Pick<User,
      'tier' | 'web_subscription' | 'is_trial' | 'trial_end_date' | 'has_used_trial'>>>) => {
      if (action.payload.userId !== state.userId) return;
      const { userId, ...membership } = action.payload;
      Object.assign(state, membership);
      if ('tier' in membership) {
        state.membershipLoaded = true;
        state.is_platinum = state.tier === 'pro';
        state.is_premium = state.tier === 'premium' || state.tier === 'ultra';
      }
    },
    update_character_tokens: (state, action: PayloadAction<{ character_tokens: number }>) => {
      console.debug('dispatched update_character_tokens', action.payload);
      state.character_tokens = action.payload.character_tokens;
    },
    refresh_stories: (state, action: PayloadAction<RefreshStoriesPayload>) => {
      console.debug('dispatched refresh_stories', action.payload);
      state.stories = action.payload.stories;
      state.last_refreshed_at = action.payload.last_refreshed_at;
      state.last_story_edited_at = action.payload.last_story_edited_at;
      if (action.payload.last_refreshed_at === null) {
        state.story_library_query = null;
        state.story_total_count = null;
      }
      if (action.payload.library_query !== undefined) {
        state.story_library_query = action.payload.library_query;
      }
      if (action.payload.total_story_count !== undefined) {
        state.story_total_count = action.payload.total_story_count;
      }
    },
    update_pagination: (state, action: PayloadAction<{ lastEvaluatedKey: any | null; donePaginating: boolean }>) => {
      console.debug('dispatched update_pagination', action.payload);
      state.lastEvaluatedKey = action.payload.lastEvaluatedKey;
      state.donePaginating = action.payload.donePaginating;
    },
    upsert_edit_story_draft: (state, action: PayloadAction<{
      key: string;
      draft: EditStoryDraftCache;
    }>) => {
      const { key, draft } = action.payload;
      if (!key) return;
      state.editStoryDrafts[key] = draft;
    },
    remove_edit_story_draft: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      if (!key) return;
      delete state.editStoryDrafts[key];
    },
    set_onboarding_seen: (state, action: PayloadAction<{ onboarding_seen: boolean }>) => {
      console.debug('dispatched set_onboarding_seen', action.payload);
      state.onboarding_seen = action.payload.onboarding_seen;
    },
    set_characters: (state, action: PayloadAction<{
      characters: Character[];
      userId?: string | null;
      refreshedAt?: number | null;
      authoritative?: boolean;
    }>) => {
      console.debug('dispatched set_characters', action.payload);
      const currentCharacters = action.payload.userId
        && state.characters_user_id
        && action.payload.userId !== state.characters_user_id
        ? []
        : state.characters;
      state.characters = mergeCharactersPreservingPending(
        currentCharacters,
        action.payload.characters,
        { authoritative: action.payload.authoritative },
      );
      state.characters_user_id = action.payload.userId ?? state.userId ?? null;
      state.characters_refreshed_at = action.payload.refreshedAt ?? Date.now();
    },
    remove_character: (state, action: PayloadAction<{ characterId: string }>) => {
      state.characters = state.characters.filter((character) => (
        (character.characterId || character.id) !== action.payload.characterId
      ));
      state.characters_refreshed_at = Date.now();
    },
    set_voices: (state, action: PayloadAction<{
      voices: Voice[];
      userId?: string | null;
      refreshedAt?: number | null;
      authoritative?: boolean;
    }>) => {
      console.debug('dispatched set_voices', action.payload);
      const currentVoices = action.payload.userId
        && state.voices_user_id
        && action.payload.userId !== state.voices_user_id
        ? []
        : state.voices;
      state.voices = mergeVoicesPreservingPending(
        currentVoices,
        action.payload.voices,
        { authoritative: action.payload.authoritative },
      );
      state.voices_user_id = action.payload.userId ?? state.userId ?? null;
      state.voices_refreshed_at = action.payload.refreshedAt ?? Date.now();
    },
    remove_voice: (state, action: PayloadAction<{ voiceId: string }>) => {
      state.voices = state.voices.filter((voice) => voice.voiceId !== action.payload.voiceId);
      state.voices_refreshed_at = Date.now();
    },
    set_book_orders: (state, action: PayloadAction<{
      orders: BookOrder[];
      userId?: string | null;
      refreshedAt?: number | null;
    }>) => {
      console.debug('dispatched set_book_orders', action.payload);
      state.book_orders = action.payload.orders;
      state.book_orders_user_id = action.payload.userId ?? state.userId ?? null;
      state.book_orders_refreshed_at = action.payload.refreshedAt ?? Date.now();
    },
    set_last_reviewed_at: (state, action: PayloadAction<{ last_reviewed_at: number }>) => {
      console.debug('dispatched set_last_reviewed_at', action.payload);
      state.last_reviewed_at = action.payload.last_reviewed_at;
    },
    set_social_accounts: (state, action: PayloadAction<{ accounts: SocialAccount[]; refreshed_at?: number | null }>) => {
      console.debug('dispatched set_social_accounts', action.payload);
      state.social_accounts = action.payload.accounts;
      state.social_accounts_refreshed_at = action.payload.refreshed_at ?? Date.now();
    },
    remove_social_account: (state, action: PayloadAction<{ accountId: string }>) => {
      console.debug('dispatched remove_social_account', action.payload);
      state.social_accounts = state.social_accounts.filter(
        (account) => account.accountId !== action.payload.accountId,
      );
      state.social_accounts_refreshed_at = Date.now();
    },
    set_notifications: (state, action: PayloadAction<Notifications>) => {
      console.debug('dispatched set_notifications', action.payload);
      state.notifications = action.payload;
    },
    set_campaign_scheduled: (state, action: PayloadAction<{ campaignScheduled: boolean }>) => {
      console.debug('dispatched set_campaign_scheduled', action.payload);
      state.campaignScheduled = action.payload.campaignScheduled;
    },
    set_trial_offer: (state, action: PayloadAction<TrialOffer | null>) => {
      console.debug('dispatched set_trial_offer', action.payload);
      state.trialOffer = action.payload;
    },
    set_rewards: (state, action: PayloadAction<{ rewards: Rewards }>) => {
      console.debug('dispatched set_rewards', action.payload);
      state.rewards = action.payload.rewards;
    },
    update_reward_progress: (state, action: PayloadAction<{ rewardType: keyof Rewards; current: number; lastVisitTime?: number }>) => {
      console.debug('dispatched update_reward_progress', action.payload);
      const { rewardType, current, lastVisitTime } = action.payload;
      if (state.rewards[rewardType]) {
        (state.rewards[rewardType] as any).current = current;
        if (lastVisitTime) {
          (state.rewards[rewardType] as any).lastVisitTime = lastVisitTime;
        }
      }
    },
    reset_reward: (state, action: PayloadAction<{ rewardType: keyof Rewards }>) => {
      console.debug('dispatched reset_reward', action.payload);
      const { rewardType } = action.payload;
      if (state.rewards[rewardType]) {
        (state.rewards[rewardType] as any).current = 0;
        (state.rewards[rewardType] as any).lastVisitTime = Date.now();
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  dismiss_gambit_banner,
  set_user,
  update_tokens,
  update_billing_account,
  update_character_tokens,
  refresh_stories,
  update_pagination,
  upsert_edit_story_draft,
  remove_edit_story_draft,
  set_onboarding_seen,
  set_characters,
  remove_character,
  set_voices,
  remove_voice,
  set_book_orders,
  set_last_reviewed_at,
  set_social_accounts,
  remove_social_account,
  set_notifications,
  set_campaign_scheduled,
  set_trial_offer,
  set_rewards,
  update_reward_progress,
  reset_reward,
} = appSlice.actions;

export default appSlice.reducer;

type MembershipState = { app: Pick<AppState, 'tier' | 'membershipLoaded' | 'is_premium' | 'is_platinum'> };
export const selectMembershipTier = ({ app }: MembershipState) => app.membershipLoaded
  ? app.tier
  : app.tier ?? (app.is_platinum ? 'pro' : app.is_premium ? 'premium' : null);
export const selectHasGambitMembership = (state: MembershipState) => {
  const tier = selectMembershipTier(state);
  return tier === 'premium' || tier === 'ultra';
};
